import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type LocationPropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Input, Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface LocationFieldProps {
  onChange: (treeDescriptor: TreeDescriptor) => void;
  placeholder?: string;
  treeDescriptor: TreeDescriptor;
}

export function LocationField<
  T extends LocationPropertyDescriptor = LocationPropertyDescriptor,
>({
  onChange,
  placeholder = getText("E.g. on a neighbour's property, near the road"),
  treeDescriptor,
}: LocationFieldProps) {
  const {
    isValid: withinBoundaryIsValid,
    messages: withinBoundaryMessages,
    shouldShowValidation,
  } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.location,
    id: "withinBoundary",
  });

  const { isValid: descriptionIsValid, messages: descriptionMessages } =
    useTreeDialogValidator<T>({
      checker: (property) => !!property?.description,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.location,
      id: "description",
    });

  const location = treeDescriptor.properties.get(TreePropertyType.location) as
    | T
    | undefined;

  const handleChangeWithinBoundary = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.location,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  const handleChangeWhere = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<LocationPropertyDescriptor>(
      treeDescriptor,
      TreePropertyType.location,
      (draft) => {
        draft.description = value;
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <>
      <div className="flex flex-col gap-1">
        <FieldLabel isValid={withinBoundaryIsValid}>
          {getText("Is this tree within the property boundaries?")}
        </FieldLabel>

        <div className="flex gap-2">
          <Toggle
            aria-invalid={!withinBoundaryIsValid && shouldShowValidation}
            className="aria-invalid:border-redBorder"
            onClick={() => {
              handleChangeWithinBoundary("on site");
            }}
            pressed={location?.value === "on site"}
            variant="rounded"
          >
            {getText("Yes")}
          </Toggle>
          <Toggle
            aria-invalid={!withinBoundaryIsValid && shouldShowValidation}
            className="aria-invalid:border-redBorder"
            onClick={() => {
              handleChangeWithinBoundary("off site");
            }}
            pressed={location?.value === "off site"}
            variant="rounded"
          >
            {getText("No")}
          </Toggle>
        </div>

        <FieldValidationMessages messages={withinBoundaryMessages} />
      </div>

      {location?.value === "off site" ? (
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-1">
            <FieldLabel isValid={descriptionIsValid}>
              {getText("Where is this tree?")}
            </FieldLabel>

            <Input
              className="invalid-sibling-label:border-redDefault"
              onChange={(event) => {
                handleChangeWhere(event.target.value);
              }}
              placeholder={placeholder}
              value={location.description || ""}
            />
          </div>

          <FieldValidationMessages messages={descriptionMessages} />
        </div>
      ) : null}
    </>
  );
}
